import type { CSSProperties, ElementType, FC, ReactNode } from 'react'

import { Fragment, useRef } from 'react'

import { cn } from '@/lib/utils'
import { gsap, useGsap, prefersReducedMotion } from '@/lib/gsap'
import { splitToWords } from '@/lib/split'

/* Padded on both sides: these headings run at a line-height below 1, so the
   glyphs overflow their own line box and a bare `overflow: hidden` shears the
   caps off the top. The negative margin hands the layout height back. */
const MASK_STYLE = {
    overflow: 'hidden',
    paddingTop: '0.26em',
    paddingBottom: '0.26em',
    marginTop: '-0.26em',
    marginBottom: '-0.26em'
} as const

export type SplitRun = {
    text: string
    className?: string
}

interface SplitRunsProps {
    runs: SplitRun[]
    as?: ElementType
    className?: string
    style?: CSSProperties
    stagger?: number
    duration?: number
    delay?: number
    trigger?: 'scroll' | 'mount'
    start?: string
    /* Leading indent in characters. The reference encodes it as a run of empty
       spans so the first line starts inset. */
    indent?: number
    id?: string
}

/* A per-character heading whose colour changes partway through. Every run
   shares one trigger and one continuous stagger, so the highlight reads as
   part of the same sweep rather than a second animation. */
export const SplitRuns: FC<SplitRunsProps> = ({
    runs,
    as: Tag = 'h2',
    className,
    style,
    stagger = 0.012,
    duration = 1.05,
    delay = 0,
    trigger = 'scroll',
    start = 'top 85%',
    indent = 0,
    id
}): ReactNode => {
    const scope = useRef<HTMLElement>(null)
    const label = runs.map((run) => run.text).join('')
    /* Re-runs the reveal whenever the runs are split differently, not only
       when the full text changes. Moving a space from one run to the next
       keeps the label identical but swaps every letter for a new, parked one,
       which would otherwise stay hidden below the mask. */
    const signature = runs.map((run) => `${run.className ?? ''}:${run.text}`).join('|')

    useGsap(() => {
        const root = scope.current

        if (!root) {
            return
        }

        const targets = root.querySelectorAll<HTMLElement>('[data-split-piece]')

        if (targets.length === 0) {
            return
        }

        /* Both ends are spelled out, pixel offset included. The parked
           position is written as a percentage in the markup, which the browser
           hands back to GSAP as a pixel offset, so a bare `to({ yPercent: 0 })`
           could finish with the letters still sitting below the mask and the
           heading never appearing. Zeroing `y` on the way in rules that out. */
        if (prefersReducedMotion()) {
            gsap.set(targets, { y: 0, yPercent: 0 })

            return
        }

        const from: gsap.TweenVars = { y: 0, yPercent: 140 }

        const vars: gsap.TweenVars = {
            y: 0,
            yPercent: 0,
            duration,
            delay,
            stagger,
            ease: 'noma'
        }

        if (trigger === 'mount') {
            gsap.fromTo(targets, from, vars)

            return
        }

        gsap.fromTo(targets, from, {
            ...vars,
            scrollTrigger: {
                trigger: root,
                start,
                once: true
            }
        })
    }, scope, [trigger, signature])

    return (
        <Tag
            id={id}
            ref={scope}
            className={cn('block', className)}
            style={style}
            aria-label={label}
        >
            <span aria-hidden='true'>
                {indent > 0
                    ? (
                        <span className='split-word' style={{ whiteSpace: 'pre' }}>
                            {' '.repeat(indent)}
                        </span>
                    )
                    : null}

                {runs.map((run, runIndex) => (
                    <span key={`${run.text}-${runIndex}`} className={run.className}>
                        {splitToWords(run.text).map((piece, pieceIndex) => (
                            <Fragment key={`${piece.word}-${pieceIndex}`}>
                                <span
                                    className='split-word'
                                    style={MASK_STYLE}
                                >
                                    {piece.chars.map((char, charIndex) => (
                                        <span
                                            key={`${char}-${charIndex}`}
                                            data-split-piece='char'
                                            className='split-char'
                                            style={{ transform: 'translateY(140%)' }}
                                        >
                                            {char}
                                        </span>
                                    ))}
                                </span>

                                {/* Kept in the parent line box so the text can
                                    still break and justify between words. */}
                                {piece.trailingSpace ? ' ' : null}
                            </Fragment>
                        ))}
                    </span>
                ))}
            </span>
        </Tag>
    )
}
